/**
 * Droite graduée zoomable — logique pure (Boîte à outils, 6e → 2nde).
 *
 * La fenêtre montre 10 intervalles d'un pas 1, 0,1, 0,01 ou 0,001 ; zoomer
 * sur un intervalle le découpe en 10. Tout se calcule en MILLIÈMES entiers
 * (jamais de flottant pour une graduation) ; un nombre placé est un rationnel
 * p/q (décimal ou fraction : 1/3 ne tombe jamais sur une graduation).
 * Encadrements, valeurs approchées par défaut et par excès, arrondi (au plus
 * proche ; à égale distance, on s'éloigne de zéro : 2,45 → 2,5 ; −2,45 → −2,5),
 * comparaison par le premier rang qui diffère.
 */

export const UNIT = 1000; // millièmes
export const MAX_LEVEL = 3;
export const LIMIT = 1000; // |x| ≤ 1 000

/** pas de graduation au niveau k, en millièmes : 1 000, 100, 10, 1 */
export const stepOf = (level: number) => UNIT / 10 ** level;

export const LEVEL_NAMES = ['à l’unité', 'au dixième', 'au centième', 'au millième'] as const;

export interface Num {
  p: number;
  q: number;
}

function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

export function num(p: number, q = 1): Num {
  if (q < 0) {
    p = -p;
    q = -q;
  }
  const g = gcd(p, q);
  return { p: p / g, q: q / g };
}

export const fromThousandths = (t: number) => num(t, UNIT);
export const value = (x: Num) => x.p / x.q;

/** a − b, signe seulement */
export const cmp = (a: Num, b: Num) => Math.sign(a.p * b.q - b.p * a.q);

/** décimal (au plus 3 décimales) ? → millièmes entiers */
export function thousandths(x: Num): number | null {
  return (x.p * UNIT) % x.q === 0 ? (x.p * UNIT) / x.q : null;
}

/** écriture d'un nombre de millièmes : « 3,47 », « −0,5 », « 12 » */
export function fmt(t: number): string {
  const neg = t < 0;
  const a = Math.abs(t);
  const int = Math.floor(a / UNIT);
  const dec = String(a % UNIT)
    .padStart(3, '0')
    .replace(/0+$/, '');
  return `${neg ? '−' : ''}${int}${dec ? `,${dec}` : ''}`;
}

/** écriture d'un rationnel : décimal s'il en est un (millièmes), sinon « p/q » */
export function numText(x: Num): string {
  const t = thousandths(x);
  if (t !== null) return fmt(t);
  return `${x.p < 0 ? '−' : ''}${Math.abs(x.p)}/${x.q}`;
}

/**
 * Saisie : « 3,47 », « -2.5 », « −0,125 », « 7/3 ». Au plus 3 décimales ;
 * fractions de dénominateur ≤ 100. `null` si illisible ou hors limites.
 */
export function parseNum(raw: string, opts: { negative: boolean }): Num | null {
  const s = raw.trim().replace(/\s/g, '').replace('−', '-').replace('.', ',');
  let m = /^(-?)(\d{1,4})(?:,(\d{1,3}))?$/.exec(s);
  let x: Num | null = null;
  if (m) {
    const dec = (m[3] ?? '').padEnd(3, '0');
    const t = Number(m[2]) * UNIT + Number(dec);
    x = fromThousandths(m[1] ? -t : t);
  } else {
    m = /^(-?)(\d{1,4})\/(\d{1,3})$/.exec(s);
    if (!m) return null;
    const q = Number(m[3]);
    if (q === 0 || q > 100) return null;
    x = num((m[1] ? -1 : 1) * Number(m[2]), q);
  }
  if (!opts.negative && x.p < 0) return null;
  if (Math.abs(value(x)) > LIMIT) return null;
  return x;
}

/* ---------- fenêtre ---------- */

export interface Win {
  level: number;
  /** début de la fenêtre, en millièmes (multiple de 10 × pas) */
  start: number;
}

export const ticksOf = (w: Win) =>
  Array.from({ length: 11 }, (_, i) => w.start + i * stepOf(w.level));
export const endOf = (w: Win) => w.start + 10 * stepOf(w.level);

/** ⌊x × 1 000 / d⌋ exact pour un rationnel */
function floorDiv(x: Num, d: number): number {
  return Math.floor((x.p * UNIT) / (x.q * d));
}

/** fenêtre du niveau `level` qui contient x (x au bord droit : fenêtre suivante) */
export function windowAround(level: number, x: Num): Win {
  const span = 10 * stepOf(level);
  // x = 1 000 : la fenêtre se termine à la limite, elle ne la dépasse pas
  const start = Math.min(floorDiv(x, span) * span, LIMIT * UNIT - span);
  return { level, start };
}

export function zoomIn(w: Win, i: number): Win {
  if (w.level >= MAX_LEVEL) return w;
  return { level: w.level + 1, start: w.start + i * stepOf(w.level) };
}

export function zoomOut(w: Win): Win {
  if (w.level <= 0) return w;
  const span = 10 * stepOf(w.level - 1);
  return { level: w.level - 1, start: Math.floor(w.start / span) * span };
}

/** décaler d'une fenêtre ; on ne sort pas de [−1 000 ; 1 000] (ni des négatifs si interdits) */
export function shift(w: Win, dir: -1 | 1, negative: boolean): Win {
  const span = 10 * stepOf(w.level);
  const start = w.start + dir * span;
  const min = negative ? -LIMIT * UNIT : 0;
  if (start < min || start + span > LIMIT * UNIT) return w;
  return { ...w, start };
}

export const inWindow = (w: Win, x: Num) =>
  cmp(x, fromThousandths(w.start)) >= 0 && cmp(x, fromThousandths(endOf(w))) <= 0;

/* ---------- encadrements ---------- */

export interface Bracket {
  level: number;
  /** valeurs approchées par défaut et par excès (millièmes) */
  lo: number;
  hi: number;
  /** x tombe sur une graduation de ce niveau */
  exact: boolean;
  /** arrondi (millièmes) */
  round: number;
}

export function bracket(x: Num, level: number): Bracket {
  const st = stepOf(level);
  const lo = floorDiv(x, st) * st;
  const exact = cmp(x, fromThousandths(lo)) === 0;
  const hi = exact ? lo : lo + st;
  // x − lo comparé à la moitié du pas, exactement : 2(x − lo) vs st
  const twice = num(2 * (x.p * UNIT - lo * x.q), x.q); // 2(x − lo) en millièmes
  const half = cmp(twice, num(st));
  // à égale distance, on s'éloigne de zéro
  const round = exact ? lo : half > 0 ? hi : half < 0 ? lo : x.p >= 0 ? hi : lo;
  return { level, lo, hi, exact, round };
}

/** « 3,4 < 3,47 < 3,5 » ou « 3,47 tombe sur la graduation 3,47 » */
export function bracketText(x: Num, b: Bracket): string {
  const xs = numText(x);
  if (b.exact) return `${xs} tombe sur une graduation.`;
  return `${fmt(b.lo)} < ${xs} < ${fmt(b.hi)}`;
}

/** partie entière de |x| × 10ᵏ : les chiffres de |x| jusqu'au rang k (troncature) */
function digitsUpTo(x: Num, k: number): number {
  return Math.floor((Math.abs(x.p) * 10 ** k) / x.q);
}

const RANK_NAMES = [
  'partie entière',
  'chiffre des dixièmes',
  'chiffre des centièmes',
  'chiffre des millièmes',
] as const;

/**
 * Premier rang où les écritures de deux nombres de même signe diffèrent
 * (0 = partie entière, 1 = dixièmes…) : c'est la méthode de comparaison du
 * cours, chiffre par chiffre. Signes contraires : 0. Égaux jusqu'au
 * millième : null.
 */
export function separatingLevel(a: Num, b: Num): number | null {
  if (a.p < 0 !== b.p < 0 && a.p !== 0 && b.p !== 0) return 0;
  for (let k = 0; k <= MAX_LEVEL; k++) if (digitsUpTo(a, k) !== digitsUpTo(b, k)) return k;
  return null;
}

/** pour deux nombres positifs (ou nuls) : « même partie entière (2), même chiffre des dixièmes (0) ; chiffre des centièmes : 0 < 5 » */
function digitWhy(small: Num, big: Num): string {
  const k = separatingLevel(small, big);
  if (k === null)
    return 'ils ont les mêmes chiffres jusqu’au millième : l’écart est plus petit encore';
  const digit = (x: Num, r: number) => (r === 0 ? digitsUpTo(x, 0) : digitsUpTo(x, r) % 10);
  const same = Array.from({ length: k }, (_, r) => `même ${RANK_NAMES[r]} (${digit(small, r)})`);
  const diff = `${RANK_NAMES[k]} : ${digit(small, k)} < ${digit(big, k)}`;
  return [...same, ''].join(', ').replace(/, $/, same.length ? ' ; ' : '') + diff;
}

/** comparaison expliquée chiffre par chiffre (et par la distance à zéro pour deux négatifs) */
export function compareText(a: Num, b: Num): string {
  const c = cmp(a, b);
  const as = numText(a);
  const bs = numText(b);
  if (c === 0) return `${as} = ${bs} : c’est le même point.`;
  const [small, big] = c < 0 ? [a, b] : [b, a];
  const ss = numText(small);
  const bgs = numText(big);
  if (small.p < 0 && big.p > 0)
    return `${ss} < ${bgs} : un nombre négatif est plus petit qu’un nombre positif.`;
  if (small.p < 0 && big.p === 0) return `${ss} < 0 : un nombre négatif est plus petit que 0.`;
  if (big.p <= 0) {
    // deux négatifs : le plus petit est le plus éloigné de zéro
    const as2 = num(-small.p, small.q);
    const bs2 = num(-big.p, big.q);
    return `${ss} < ${bgs} : deux nombres négatifs, le plus petit est le plus éloigné de zéro. Distances à zéro : ${numText(bs2)} < ${numText(as2)} (${digitWhy(bs2, as2)}).`;
  }
  return `${ss} < ${bgs} : ${digitWhy(small, big)}.${expansions(small, big)}`;
}

/** « (1/3 = 0,3333…) » pour un nombre qui n'est pas décimal */
function expansions(...xs: Num[]): string {
  const out = xs
    .filter((x) => thousandths(x) === null)
    .map((x) => {
      const t = digitsUpTo(x, 4);
      const int = Math.floor(t / 10000);
      const dec = String(t % 10000).padStart(4, '0');
      return `${numText(x)} = ${x.p < 0 ? '−' : ''}${int},${dec}…`;
    });
  return out.length ? ` (${out.join(' ; ')})` : '';
}

/* ---------- défi : placer un nombre ---------- */

export type Feedback =
  | { kind: 'ok' }
  | { kind: 'zoom'; lo: number; hi: number }
  | { kind: 'left' | 'right'; placed: number };

/** l'élève a posé la graduation `t` (millièmes) pour la cible `target` */
export function placeFeedback(target: Num, t: number, level: number): Feedback {
  const c = cmp(target, fromThousandths(t));
  if (c === 0) return { kind: 'ok' };
  const st = stepOf(level);
  if (c > 0 && cmp(target, fromThousandths(t + st)) < 0) return { kind: 'zoom', lo: t, hi: t + st };
  if (c < 0 && cmp(target, fromThousandths(t - st)) > 0) return { kind: 'zoom', lo: t - st, hi: t };
  return { kind: c > 0 ? 'right' : 'left', placed: t };
}

/** cible du défi selon le niveau (rang 1 = 6e) ; `rnd` dans [0 ; 1[ */
export function challengeTarget(rank: number, rnd: () => number): Num {
  const int = (a: number, b: number) => a + Math.floor(rnd() * (b - a + 1));
  if (rank <= 1) {
    // 6e : décimal positif à 2 décimales, entre 0 et 20
    return fromThousandths(int(1, 1999) * 10);
  }
  if (rank === 2) {
    // 5e : relatif à 1 ou 2 décimales entre −10 et 10
    const t = rnd() < 0.5 ? int(-99, 99) * 100 : int(-999, 999) * 10;
    return fromThousandths(t === 0 ? 250 : t);
  }
  // 4e et au-delà : jusqu'au millième
  const t = int(-9999, 9999);
  return fromThousandths(t === 0 ? 125 : t);
}
